<template>
  <div class="address-autocomplete">
    <input
      ref="inputRef"
      v-model="inputValue"
      type="text"
      :placeholder="placeholder || t('fields.enterAddress')"
      autocomplete="off"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.down.prevent="navigateSuggestions(1)"
      @keydown.up.prevent="navigateSuggestions(-1)"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.escape="closeSuggestions"
    />
    <ul
      v-if="displaySuggestions.length > 0 && showSuggestions"
      class="suggestions-list"
      role="listbox"
    >
      <li
        v-for="(item, index) in displaySuggestions"
        :key="item.id"
        :class="{ highlighted: index === highlightedIndex }"
        role="option"
        @mousedown.prevent="selectSuggestion(item)"
        @mouseenter="highlightedIndex = index"
      >
        {{ item.text }}
      </li>
    </ul>
    <div v-if="isLoading" class="loading-indicator">
      <span>{{ t('menu.loading') }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, shallowRef, computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

// Maps JS is loaded by the parent via useAddressAutocomplete (key from
// EZCL_GOOGLE_MAPS_API_KEY); this component only consumes the ready API and
// reports failures through the `error` emit so the parent can degrade.

const { t } = useI18n()

// Props
const props = defineProps({
  placeholder: {
    type: String,
    default: ''
  },
  // Optional: restrict to specific countries (ISO 3166-1 Alpha-2 codes)
  countries: {
    type: Array,
    default: () => []
  },
  // Minimum characters before triggering autocomplete
  minChars: {
    type: Number,
    default: 4
  },
  // Debounce delay in milliseconds
  debounceMs: {
    type: Number,
    default: 400
  },
  // Gate from useAddressAutocomplete: returns false when the per-page session
  // ceiling is hit. Defaults to always-allow when used standalone.
  allowSession: {
    type: Function,
    default: () => true
  }
})

// Emits
const emit = defineEmits(['select', 'error', 'clear'])

// Refs
const inputRef = ref(null)
const inputValue = ref('')
const suggestions = shallowRef([])
const showSuggestions = ref(false)
const highlightedIndex = ref(-1)
const isLoading = ref(false)

// Computed: transform raw suggestions into a safe format for rendering
const displaySuggestions = computed(() => {
  return suggestions.value
    // Дополнительная защита: проверяем сам объект и наличие placePrediction
    .filter(s => s && s.placePrediction) 
    .map((suggestion, index) => {
      // Сохраняем ссылку на prediction для удобства
      const prediction = suggestion.placePrediction;
      return {
        raw: suggestion,
        placePrediction: prediction,
        // Используем безопасный доступ
        id: prediction.placeId || `suggestion-${index}`,
        text: prediction.text ? prediction.text.toString() : '' 
      }
    })
})
// Session Management
// ------------------
// Session tokens optimize billing by grouping autocomplete requests with
// the final place details request. A session starts when the user begins
// typing and ends when they select a place or abandon the search.
let sessionToken = null
let debounceTimer = null

// Cost guards (per session): skip an identical repeated query, and serve a
// query already fetched in this session from memory instead of re-billing.
let lastQuery = ''
let sessionCache = new Map()

/**
 * Check if Google Maps Places API (New) is available
 */
const isApiLoaded = () => {
  return (
    typeof google !== 'undefined' &&
    google.maps?.places?.AutocompleteSessionToken &&
    google.maps?.places?.AutocompleteSuggestion
  )
}

/**
 * Create a new session token
 * Called when user starts a new autocomplete session (focus or first keystroke)
 */
const createSession = () => {
  if (!isApiLoaded()) return
  if (!props.allowSession()) return // per-page ceiling hit → parent degrades
  sessionToken = new google.maps.places.AutocompleteSessionToken()
}

/**
 * End the current session
 * Called after place selection or when abandoning the search.
 */
const endSession = () => {
  sessionToken = null
  lastQuery = ''
  sessionCache.clear()
}

/**
 * Handle input focus - start a new session if none exists
 */
const handleFocus = () => {
  showSuggestions.value = true
  // Start a new session on focus if we don't have one
  if (!sessionToken && inputValue.value.length >= props.minChars) {
    createSession()
  }
}

/**
 * Handle input blur - hide suggestions after a short delay
 * Delay allows click events on suggestions to fire first
 */
const handleBlur = () => {
  setTimeout(() => {
    showSuggestions.value = false
    highlightedIndex.value = -1
  }, 150)
}

/**
 * Handle input changes with debouncing
 */
const handleInput = () => {
  highlightedIndex.value = -1

  // Create session on first meaningful input
  if (!sessionToken && inputValue.value.length >= props.minChars) {
    createSession()
  }

  // Clear suggestions if input is too short
  if (inputValue.value.length < props.minChars) {
    suggestions.value = []
    if (inputValue.value.length === 0) {
      endSession()
      emit('clear')
    }
    return
  }

  // Debounce autocomplete requests
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }

  debounceTimer = setTimeout(fetchSuggestions, props.debounceMs)
}

/**
 * Fetch autocomplete suggestions from Google Places API (New)
 */
const fetchSuggestions = async () => {
  if (!inputValue.value || inputValue.value.length < props.minChars) {
    suggestions.value = []
    return
  }

  if (!isApiLoaded()) {
    console.warn('Google Maps Places API (New) is not loaded')
    emit('error', new Error('Google Maps API not loaded'))
    return
  }

  const query = inputValue.value.trim()

  // Dedup: identical query already in flight/shown → no new billable request.
  if (query === lastQuery) return

  // Memoize: same query seen earlier this session → serve from memory.
  if (sessionCache.has(query)) {
    lastQuery = query
    suggestions.value = sessionCache.get(query)
    return
  }

  // Ensure we have a session token (may be refused at the page ceiling).
  if (!sessionToken) {
    createSession()
  }
  if (!sessionToken) return

  lastQuery = query
  isLoading.value = true

  try {
    const request = {
      input: query,
      sessionToken: sessionToken
    }

    // Add country restrictions if specified
    if (props.countries.length > 0) {
      request.includedRegionCodes = props.countries
    }

    // Fetch suggestions using the new API
    const response = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
    
    // Handle different response formats
    // The response may have suggestions directly or nested
    let rawSuggestions = response.suggestions || response || []
    
    // Ensure we have an array
    if (!Array.isArray(rawSuggestions)) {
      rawSuggestions = []
    }

    sessionCache.set(query, rawSuggestions)
    suggestions.value = rawSuggestions
  } catch (error) {
    console.error('Autocomplete error:', error)
    suggestions.value = []
    lastQuery = '' // allow a retry of this query after a transient failure
    emit('error', error)
  } finally {
    isLoading.value = false
  }
}

/**
 * Select a suggestion and fetch place details
 * This completes the session, bundling all requests for optimal billing
 */
const selectSuggestion = async (item) => {
  const { placePrediction, text } = item
  if (!placePrediction) return

  inputValue.value = text
  suggestions.value = []
  showSuggestions.value = false
  highlightedIndex.value = -1
  isLoading.value = true

  try {
    // Use toPlace() method to get Place object from prediction
    const place = placePrediction.toPlace()

    // Fetch place details using the SAME session token
    // This completes the autocomplete session and optimizes billing
    // Only request addressComponents (Essentials SKU) to minimize costs
    await place.fetchFields({
      fields: ['addressComponents', 'formattedAddress', 'location'],
      sessionToken: sessionToken
    })

    // Extract structured address components. place.location holds the lat/lng
    // (Places API New); wrap it as { location } for the extractor.
    const addressData = extractAddressComponents(
      place.addressComponents,
      place.location ? { location: place.location } : null
    )

    emit('select', {
      placeId: placePrediction.placeId,
      formattedAddress: place.formattedAddress || text,
      ...addressData
    })
  } catch (error) {
    console.error('Place details error:', error)
    emit('error', error)
  } finally {
    isLoading.value = false
    // Session ends after place selection
    endSession()
  }
}

/**
 * Extract address components into a structured format
 */
const extractAddressComponents = (components, geometry = null) => {
  const result = {
    streetNumber: '',
    route: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    countryCode: '', // Полезно для валидации
    lat: null,
    lng: null
  }

  if (geometry && geometry.location) {
    result.lat = typeof geometry.location.lat === 'function' ? geometry.location.lat() : geometry.location.lat;
    result.lng = typeof geometry.location.lng === 'function' ? geometry.location.lng() : geometry.location.lng;
  }

  if (!components) return result

  // Хелпер для поиска компонента по типу
  const getComponent = (type, useShort = false) => {
    const comp = components.find(c => c.types.includes(type))
    return comp ? (useShort ? comp.shortText : comp.longText) : ''
  }

  result.streetNumber = getComponent('street_number')
  result.route = getComponent('route')
  
  // Логика поиска города: locality -> postal_town -> sublocality -> administrative_area_level_2
  result.city = getComponent('locality') || 
                getComponent('postal_town') || 
                getComponent('sublocality_level_1') ||
                getComponent('administrative_area_level_2');

  result.state = getComponent('administrative_area_level_1', true) // true для короткого имени (код штата/региона)
  result.postalCode = getComponent('postal_code')
  result.country = getComponent('country')
  result.countryCode = getComponent('country', true) // 'US', 'UA'

  return result
}

/**
 * Keyboard navigation for suggestions
 */
const navigateSuggestions = (direction) => {
  if (displaySuggestions.value.length === 0) return

  const newIndex = highlightedIndex.value + direction
  if (newIndex >= 0 && newIndex < displaySuggestions.value.length) {
    highlightedIndex.value = newIndex
  } else if (newIndex < 0) {
    highlightedIndex.value = displaySuggestions.value.length - 1
  } else {
    highlightedIndex.value = 0
  }
}

/**
 * Select the currently highlighted suggestion
 */
const selectHighlighted = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < displaySuggestions.value.length) {
    selectSuggestion(displaySuggestions.value[highlightedIndex.value])
  }
}

/**
 * Close suggestions dropdown
 */
const closeSuggestions = () => {
  showSuggestions.value = false
  highlightedIndex.value = -1
}

/**
 * Expose methods for parent component access
 */
defineExpose({
  clear: () => {
    inputValue.value = ''
    suggestions.value = []
    endSession()
    emit('clear')
  },
  focus: () => inputRef.value?.focus()
})

// Cleanup on unmount
onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  endSession()
})
</script>

<style scoped>
.address-autocomplete {
  position: relative;
  width: 100%;
}

.address-autocomplete input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  line-height: 1.5;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.address-autocomplete input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 240px;
  overflow-y: auto;
  z-index: 1000;
}

.suggestions-list li {
  padding: 0.625rem 1rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #374151;
  transition: background-color 0.1s ease;
}

.suggestions-list li:hover,
.suggestions-list li.highlighted {
  background-color: #f3f4f6;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: #6b7280;
}
</style>
